import { FormGroup } from '@angular/forms';

type WithId = { id: unknown };

function isObjectWithId(value: unknown): value is WithId {
  return !!value && typeof value === 'object' && Object.prototype.hasOwnProperty.call(value, 'id');
}

function isStringArray(options: unknown[]): options is string[] {
  return Array.isArray(options) && options.every((o) => typeof o === 'string');
}

function handleStringOptions(formGroup: FormGroup, controlName: string, options: string[], value: unknown): void {
  const control = formGroup.controls[controlName];
  control.setValue(typeof value === 'string' && options.includes(value) ? value : '');
}

function resetFallbackControls(formGroup: FormGroup, fallbackControls: string[]): void {
  fallbackControls.forEach((fcName) => formGroup.get(fcName)?.setValue(null));
}

function matchesItem(item: unknown, value: unknown, matchProp?: string): boolean {
  if (typeof item !== 'object' || item === null) {
    return item === value;
  }

  const obj = item as Record<string, unknown>;

  if (matchProp) {
    return matchProp in obj && obj[matchProp] === value;
  }

  return Object.values(obj).some((v) => v === value);
}

export function normalizeAutocompleteValue<T>(
  formGroup: FormGroup,
  controlName: string,
  options: T[],
  matchProp?: keyof T,
  fallbackControlsToReset: string[] = [],
): void {
  const control = formGroup.controls[controlName];
  const value = control?.value as unknown;

  if (!options || !Array.isArray(options)) {
    return;
  }

  if (isStringArray(options as unknown[])) {
    handleStringOptions(formGroup, controlName, options as unknown as string[], value);
    return;
  }

  if (isObjectWithId(value)) {
    return;
  }

  const match = (options as unknown[]).find((item) => matchesItem(item, value, matchProp as string));

  if (!match) {
    control.setValue('');
    resetFallbackControls(formGroup, fallbackControlsToReset);
  } else {
    control.setValue(match as T);
  }
}
